import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { MdDelete, MdEdit, MdHistory } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import CreateWorkflowModal from "./CreateWorkflowModal";
import { STATUS_COLOR, TRIGGER_LABELS } from "./templates";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import { FeatureUpgradeContent } from "../shared/FeatureUpgradeModal";

export default function WorkflowList() {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [status, setStatus] = useState("idle");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { state } = useContext(SettingContext) || { state: {} };
  const hasFeature = !!(
    state?.credits?.workflows === true ||
    (typeof state?.credits?.workflows === "number" && state.credits.workflows >= 1)
  );

  async function fetchAll() {
    setStatus("loading");
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/workflow?size=100`,
        "GET"
      );
      setWorkflows(res?.data?.items || []);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  useEffect(() => { if (hasFeature) fetchAll(); }, [hasFeature]); // eslint-disable-line react-hooks/exhaustive-deps

  // All hooks above run unconditionally — early-return is safe below.
  if (!hasFeature) {
    return (
      <Box pt={10}>
        <Card variant="elevated">
          <CardBody>
            <FeatureUpgradeContent
              title="Workflows"
              description="Automate inbound call routing and outbound reminder sequences. Build no-code flows that trigger on call, booking, or schedule — cut no-shows with SMS reminders and route calls intelligently by time of day."
              availablePlans={["Ultra", "Agency Pro"]}
              variant="inline"
            />
          </CardBody>
        </Card>
      </Box>
    );
  }

  async function handleDelete(wf) {
    if (!window.confirm(`Delete workflow "${wf.name}"? This cannot be undone.`)) return;
    try {
      await fetchController(`${baseUrl}/api/v1/workflow/${wf.id}`, "DELETE");
      fetchAll();
    } catch (e) {
      alert("Delete failed: " + (e?.message || "unknown error"));
    }
  }

  const loading = status === "loading";

  return (
    <VStack align="stretch" spacing={4} h="100%">
      <Flex align="center" justify="space-between">
        <Box>
          <Heading size="md">Workflows</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Event-driven automations — inbound call routing, booking reminders, etc.
          </Text>
        </Box>
        <Button colorScheme="blue" onClick={onOpen}>
          + New Workflow
        </Button>
      </Flex>

      <Card variant="elevated">
        <CardBody p={0}>
          {loading ? (
            <Flex justify="center" py={10}><Spinner /></Flex>
          ) : workflows.length === 0 ? (
            <Flex direction="column" align="center" justify="center" py={10} gap={2}>
              <Text color="gray.500">No workflows yet</Text>
              <Button size="sm" colorScheme="blue" onClick={onOpen}>
                Create your first workflow
              </Button>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Trigger</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Priority</Th>
                    <Th isNumeric>Version</Th>
                    <Th w="160px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {workflows.map((w) => (
                    <Tr key={w.id} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="medium">{w.name}</Td>
                      <Td>
                        <Badge colorScheme="purple" fontSize="xs">
                          {TRIGGER_LABELS[w.trigger] || w.trigger}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={STATUS_COLOR[w.status] || "gray"} fontSize="xs">
                          {w.status}
                        </Badge>
                      </Td>
                      <Td isNumeric color="gray.500">{w.priority}</Td>
                      <Td isNumeric color="gray.500">v{w.version}</Td>
                      <Td>
                        <Flex gap={2} justify="flex-end">
                          <Tooltip label="View runs">
                            <IconButton
                              size="xs"
                              variant="ghost"
                              icon={<MdHistory />}
                              aria-label="Runs"
                              onClick={() => navigate(`/library/workflows/${w.id}/runs`)}
                            />
                          </Tooltip>
                          <Tooltip label="Edit">
                            <IconButton
                              size="xs"
                              icon={<MdEdit />}
                              aria-label="Edit"
                              onClick={() => navigate(`/library/workflows/${w.id}`)}
                            />
                          </Tooltip>
                          <Tooltip label="Delete">
                            <IconButton
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              icon={<MdDelete />}
                              aria-label="Delete"
                              onClick={() => handleDelete(w)}
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      <CreateWorkflowModal
        isOpen={isOpen}
        onClose={onClose}
        onCreated={(wf) => {
          onClose();
          navigate(`/library/workflows/${wf.id}`);
        }}
      />
    </VStack>
  );
}
